// Shared receipt building blocks so the Approval modal and the Transaction-detail
// modal render the parcel breakdown, payment lines (with slips) and the full-screen
// slip viewer identically.
import { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Dialog,
  DialogContent,
  DialogActions,
  Divider,
  CircularProgress,
  IconButton,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import {
  Close,
  Inventory2Outlined,
  ReceiptLongOutlined,
  PaymentsOutlined,
  AccountBalanceOutlined,
  PersonOutlineOutlined,
  ZoomInRounded,
} from "@mui/icons-material";
import AuthenticatedImage from "components/AuthenticatedImage";
import { evidenceUrl } from "api/financial";
import TrackingDetailDialog from "components/TrackingDetialDialog.";

export const SYM = { THB: "฿", LAK: "₭", USD: "$" };
export const MONO = "'Space Grotesk', 'Noto Sans Lao', ui-monospace, monospace";
export const money = (n, c = "THB") =>
  `${SYM[c] || ""}${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
export const fmtDate = (d) => (d ? new Date(d).toLocaleString() : "—");

// Per-method visual identity so the payment channel reads at a glance.
export const METHOD_STYLE = {
  cash: { label: "Cash", Icon: PaymentsOutlined, fg: "#15a36a", bg: "#e7f8ee" },
  bank_transfer: { label: "Bank transfer", Icon: AccountBalanceOutlined, fg: "#1d4ed8", bg: "#dbeafe" },
};

// A plain label/value row (no card border) — used inside a bordered container
// for compact key-value lists, e.g. a "Change Given" breakdown.
export function MetaRow({ label, value, valueColor }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, py: 0.5 }}>
      <Typography sx={{ fontSize: 12.5 }} color="text.secondary">{label}</Typography>
      <Typography sx={{ fontSize: 12.5, fontWeight: 600, textAlign: "right" }} color={valueColor || "text.primary"}>
        {value || "—"}
      </Typography>
    </Box>
  );
}

// The bordered "hero number + meta grid" box at the top of every event detail
// dialog (Approval request, Top Up detail): a big headline figure on the left
// (fee due / amount credited) with an optional chip/description under it, and
// a stack of MetaRows (customer, station, date, ...) on the right.
export function HeroMetaHeader({ heroLabel, heroValue, heroColor = "secondary.main", heroExtra, metaRows = [] }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "auto 1fr" },
        gap: 2,
        p: 2,
        borderRadius: "14px",
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.default",
      }}
    >
      <Box>
        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase" }} color="text.secondary">
          {heroLabel}
        </Typography>
        <Typography sx={{ fontFamily: MONO, fontWeight: 800, fontSize: 30, lineHeight: 1.1 }} color={heroColor}>
          {heroValue}
        </Typography>
        {heroExtra}
      </Box>
      <Box>
        {metaRows.map((r, i) => (
          <MetaRow key={i} {...r} />
        ))}
      </Box>
    </Box>
  );
}

// Common modal shell for "one business event, several ledger legs" detail
// dialogs (Approval request for a Pickup, Top Up detail) — header with
// title/reference + close button, a loading spinner, an error state, and a
// footer Close button with room for extra actions (e.g. Approve/Reject).
// Each caller supplies its own content (hero+meta header, items table,
// payment lines, change given, related transactions, ...) as `children`.
export function DetailDialogShell({ t, open, onClose, title, subtitle, loading, empty, emptyLabel, children, actions }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: "18px" } }}>
      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5 }}>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 18 }}>{title}</Typography>
            {subtitle && (
              <Typography sx={{ fontFamily: MONO, fontSize: 12 }} color="text.secondary">{subtitle}</Typography>
            )}
          </Box>
          <IconButton size="small" onClick={onClose}><Close fontSize="small" /></IconButton>
        </Box>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress color="secondary" /></Box>
        )}

        {!loading && !empty && children}

        {!loading && empty && (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>{emptyLabel || t("common.error")}</Typography>
        )}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5 }}>
        <Button variant="outlined" color="secondary" onClick={onClose} sx={{ borderRadius: "12px", fontWeight: 700, px: 2.5 }}>
          {t("common.close")}
        </Button>
        {actions && <Box sx={{ flex: 1 }} />}
        {actions}
      </DialogActions>
    </Dialog>
  );
}

export function SectionLabel({ Icon, children, count }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2.5, mb: 1 }}>
      <Icon fontSize="small" color="secondary" />
      <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>
        {children}
        {count != null && ` (${count})`}
      </Typography>
    </Stack>
  );
}

// Per-source visual identity for a single-line payment (see SinglePaymentCard)
// keyed by WalletTopupRequest.paymentSource rather than a ShippingPayment line's
// method — separate map from METHOD_STYLE above since the two records don't
// share a vocabulary (cash_store/bank_transfer/staff_advance vs cash/bank_transfer).
export const SOURCE_STYLE = {
  bank_transfer: { Icon: AccountBalanceOutlined, fg: "#1d4ed8", bg: "#dbeafe" },
  staff_advance: { Icon: PersonOutlineOutlined, fg: "#a85c00", bg: "#fdf1de" },
  cash_store: { Icon: PaymentsOutlined, fg: "#15a36a", bg: "#e7f8ee" },
};

// A single payment method card (icon + label + amount + evidence, click to
// enlarge) for a business event that only ever has ONE payment line — e.g. a
// cash top-up — as opposed to ShippingPayment's PaymentLines below, which
// renders potentially several cash+bank lines. Deliberately does NOT show a
// "= ฿X" THB-equivalent line the way PaymentLines does for a foreign-currency
// line: the amount here is real money handed over, but there is no accurate
// historical exchange rate captured for it anywhere on a top-up record (unlike
// a ShippingPayment line's own stored `amountTHB`) — showing a guessed
// conversion would just be wrong, so this component always omits it.
export function SinglePaymentCard({ t, label, currency, amount, style, photos = [], onEnlarge, getEvidenceUrl = evidenceUrl }) {
  const Icon = style.Icon;
  return (
    <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: "14px", overflow: "hidden", maxWidth: { sm: "50%" } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, p: 1.5, background: style.bg }}>
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: "12px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: style.fg, color: "#fff" }}>
            <Icon sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: 15, color: style.fg, lineHeight: 1.1 }}>{label}</Typography>
            <Typography sx={{ fontSize: 11, fontWeight: 600 }} color="text.secondary">{currency}</Typography>
          </Box>
        </Stack>
        <Typography sx={{ fontFamily: MONO, fontSize: 16, fontWeight: 800, color: style.fg }}>{money(amount, currency)}</Typography>
      </Box>
      {photos.length > 0 ? (
        <Box sx={{ p: 1.5, display: "grid", gridTemplateColumns: photos.length > 1 ? "1fr 1fr" : "1fr", gap: 1 }}>
          {photos.map((photo, pi) => (
            <Box
              key={photo.filename ?? pi}
              onClick={() => onEnlarge && onEnlarge(photo.filename)}
              sx={{ position: "relative", cursor: "zoom-in", "&:hover .slipHint": { opacity: 1 } }}
            >
              <AuthenticatedImage
                src={getEvidenceUrl(photo.filename)}
                alt={t("financial.slip")}
                style={{ width: "100%", height: "auto", maxHeight: "40vh", objectFit: "contain", borderRadius: 8, border: "1px solid #eee", display: "block" }}
              />
              <Box
                className="slipHint"
                sx={{
                  position: "absolute", bottom: 14, right: 14,
                  display: "flex", alignItems: "center", gap: 0.5,
                  px: 1, py: 0.5, borderRadius: "8px",
                  background: "rgba(20,18,30,.72)", color: "#fff", fontSize: 11, fontWeight: 600,
                  opacity: 0.85, transition: "opacity .15s", pointerEvents: "none",
                }}
              >
                <ZoomInRounded sx={{ fontSize: 15 }} />
                {t("financial.enlarge")}
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <Box sx={{ p: 1.5 }}>
          <Typography sx={{ fontSize: 11.5 }} color="text.secondary">{t("financial.noSlip")}</Typography>
        </Box>
      )}
    </Box>
  );
}

// Itemized "change given back to the customer" box — one MetaRow per line,
// shared by ShippingApprovalDetailDialog (lines carry a `source`: "cash" or
// "wallet_credit") and the Top Up detail (lines are always cash, no `source`
// field at all — a top-up can't credit-back into the very wallet it just
// topped up). `labelFor` lets each caller supply its own per-line label;
// defaults to "Cash" for callers (like Top Up) that have nothing to distinguish.
export function ChangeGivenBox({ t, lines = [], labelFor = () => t("pickup.payment.changeCash") }) {
  if (lines.length === 0) return null;
  return (
    <Box sx={{ mt: 2, p: 1.5, borderRadius: "12px", border: "1px solid", borderColor: "divider", bgcolor: "background.default" }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", mb: 0.5 }} color="text.secondary">
        {t("pickup.payment.changeGiven")}
      </Typography>
      {lines.map((l, i) => (
        <MetaRow key={i} label={labelFor(l)} value={`-${money(l.amount, l.currency)}`} valueColor="error.main" />
      ))}
    </Box>
  );
}

// A ref can be a plain id string (ShippingPayment.transactionRefs), a pre-labeled
// row the caller already built ({id, label, amount, currency, sign} — see
// ShippingApprovalDetailDialog, which reconstructs each leg's meaning from the
// payment it belongs to), or the richer shape backend's findRelatedTransactionRefs
// returns ({id, ledgerType, txn_type, amount, currency, purpose} — Top Up detail).
// Normalized to one shape so RelatedTransactionsBox can render either identically.
function normalizeTransactionRef(r) {
  if (typeof r === "string") return { id: r };
  if (r.label !== undefined) return r;
  return {
    id: r.id,
    label: r.purpose ? r.purpose.charAt(0).toUpperCase() + r.purpose.slice(1) : undefined,
    amount: r.amount,
    currency: r.currency,
    sign: r.txn_type === "debit" ? "-" : "+",
  };
}

// "Completed"/"Rejected" status box + one itemized row per ledger leg a business
// event settled to — each leg's own label/amount (when known) shown directly so
// staff can see what happened without opening every transaction, still tappable
// to view full detail. `refs` empty + no `statusLabel` renders nothing.
export function RelatedTransactionsBox({ t, ok, statusLabel, subLabel, refs = [], onView }) {
  const rows = refs.map(normalizeTransactionRef).filter((r) => r.id);
  if (!statusLabel && rows.length === 0) return null;
  return (
    <Box
      sx={{
        mt: 2, p: 1.5, borderRadius: "12px", border: "1px solid",
        borderColor: ok ? "success.main" : "error.main",
        bgcolor: ok ? "rgba(46,125,50,0.06)" : "rgba(211,47,47,0.06)",
      }}
    >
      {statusLabel && (
        <Typography sx={{ fontWeight: 700, fontSize: 13 }} color={ok ? "success.main" : "error.main"}>
          {statusLabel}
        </Typography>
      )}
      {subLabel && (
        <Typography sx={{ fontSize: 12.5, mt: 0.5 }} color="text.secondary">{subLabel}</Typography>
      )}
      {rows.length > 0 && (
        <Stack spacing={0.75} sx={{ mt: 1.25 }}>
          {rows.map((r, i) => (
            <Box
              key={r.id}
              onClick={() => onView(r.id)}
              sx={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                p: "8px 10px", borderRadius: "10px", border: "1px solid", borderColor: "divider",
                bgcolor: "background.paper", cursor: "pointer",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <Typography sx={{ fontSize: 12.5, fontWeight: 600 }} color="text.primary">
                {r.label || `${t("financial.viewTransaction")}${rows.length > 1 ? ` ${i + 1}` : ""}`}
              </Typography>
              {r.amount != null && (
                <Typography sx={{ fontFamily: MONO, fontSize: 12.5, fontWeight: 700 }} color={r.sign === "-" ? "error.main" : "success.main"}>
                  {r.sign === "-" ? "-" : "+"}{money(r.amount, r.currency)}
                </Typography>
              )}
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}

// Parcels covered by the payment, each with COD value + shipping fee + row total.
// `serviceFeeTHB` is the flat delivery-service fee (see collectShippingPayment's
// serviceType:"delivery") — it isn't tied to any single parcel, so it renders as
// its own row rather than folding into a per-parcel column.
export function ParcelsTable({ t, parcels = [], totalTHB, serviceFeeTHB = 0 }) {
  const [trackingOpen, setTrackingOpen] = useState(null); // tracking number shown in the drill-down dialog
  const sumCod = parcels.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const sumShip = parcels.reduce((s, r) => s + (Number(r.shippingFee) || 0), 0);
  const grand = totalTHB != null ? totalTHB : sumCod + sumShip + serviceFeeTHB;
  return (
    <>
      <SectionLabel Icon={Inventory2Outlined} count={parcels.length}>{t("financial.items")}</SectionLabel>
      <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: "12px", overflow: "hidden" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ "& th": { fontWeight: 700, fontSize: 11.5, color: "text.secondary", bgcolor: "background.default" } }}>
              <TableCell>{t("financial.tracking")}</TableCell>
              <TableCell align="right">{t("financial.goodsValue")}</TableCell>
              <TableCell align="right">{t("financial.shippingFee")}</TableCell>
              <TableCell align="right">{t("financial.rowTotal")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {parcels.map((r) => {
              const cod = Number(r.amount) || 0;
              const ship = Number(r.shippingFee) || 0;
              return (
                <TableRow
                  key={r._id || r.tracking}
                  onClick={() => r.tracking && setTrackingOpen(r.tracking)}
                  sx={r.tracking ? { cursor: "pointer", "&:hover": { bgcolor: "action.hover" } } : undefined}
                >
                  <TableCell>
                    <Typography sx={{ fontFamily: MONO, fontSize: 12.5, fontWeight: 600 }} color="secondary.main">{r.tracking}</Typography>
                    {r.description && (
                      <Typography
                        sx={{
                          fontSize: 11,
                          // Show the full description: wrap onto as many lines as needed
                          // (no truncation) and break long unbroken tokens so they stay
                          // inside the cell instead of overflowing the dialog.
                          whiteSpace: "normal",
                          overflowWrap: "anywhere",
                          wordBreak: "break-word",
                        }}
                        color="text.secondary"
                      >
                        {r.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right"><Typography sx={{ fontFamily: MONO, fontSize: 12.5 }}>{money(cod)}</Typography></TableCell>
                  <TableCell align="right"><Typography sx={{ fontFamily: MONO, fontSize: 12.5 }}>{money(ship)}</Typography></TableCell>
                  <TableCell align="right"><Typography sx={{ fontFamily: MONO, fontSize: 12.5, fontWeight: 700 }}>{money(cod + ship)}</Typography></TableCell>
                </TableRow>
              );
            })}
            {serviceFeeTHB > 0 && (
              <TableRow>
                <TableCell><Typography sx={{ fontSize: 12.5, fontWeight: 600 }} color="secondary.main">{t("financial.deliveryServiceFee")}</Typography></TableCell>
                <TableCell align="right">—</TableCell>
                <TableCell align="right">—</TableCell>
                <TableCell align="right"><Typography sx={{ fontFamily: MONO, fontSize: 12.5, fontWeight: 700 }} color="secondary.main">{money(serviceFeeTHB)}</Typography></TableCell>
              </TableRow>
            )}
            <TableRow sx={{ "& td": { borderTop: "2px solid", borderColor: "divider" } }}>
              <TableCell><Typography sx={{ fontSize: 12, fontWeight: 700 }}>{t("financial.rowTotal")}</Typography></TableCell>
              <TableCell align="right"><Typography sx={{ fontFamily: MONO, fontSize: 12.5, fontWeight: 700 }}>{money(sumCod)}</Typography></TableCell>
              <TableCell align="right"><Typography sx={{ fontFamily: MONO, fontSize: 12.5, fontWeight: 700 }}>{money(sumShip)}</Typography></TableCell>
              <TableCell align="right"><Typography sx={{ fontFamily: MONO, fontSize: 12.5, fontWeight: 800 }} color="secondary.main">{money(grand)}</Typography></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>
      <TrackingDetailDialog open={!!trackingOpen} trackingNumber={trackingOpen} onClose={() => setTrackingOpen(null)} hideEditHistory />
    </>
  );
}

// Cash/bank lines with a prominent method header and the attached slip (click to
// enlarge via `onEnlarge(filename)`). `getEvidenceUrl` defaults to the manager-scoped
// /api/financial/evidence — pass `branchCashEvidenceUrl` (api/branchCash) for the
// station-scoped Branch Cash page.
export function PaymentLines({ t, lines = [], onEnlarge, getEvidenceUrl = evidenceUrl, emptyLabel }) {
  return (
    <>
      <SectionLabel Icon={ReceiptLongOutlined}>{t("financial.paymentLines")}</SectionLabel>
      {lines.length === 0 ? (
        <Typography sx={{ fontSize: 12.5 }} color="text.secondary">{emptyLabel || t("financial.noSlip")}</Typography>
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
          {lines.map((l, i) => {
            const ms = METHOD_STYLE[l.method] || { label: l.method, Icon: PaymentsOutlined, fg: "#6d28d9", bg: "#f3f0fc" };
            const Icon = ms.Icon;
            // Multiple evidence photos are supported; older records only have the
            // single `evidencePhoto` — normalize both shapes.
            const photos = l.evidencePhotos?.length ? l.evidencePhotos : (l.evidencePhoto?.filename ? [l.evidencePhoto] : []);
            return (
              <Box key={i} sx={{ border: "1px solid", borderColor: "divider", borderRadius: "14px", overflow: "hidden" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, p: 1.5, background: ms.bg }}>
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: "12px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: ms.fg, color: "#fff" }}>
                      <Icon sx={{ fontSize: 22 }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 800, fontSize: 15, color: ms.fg, lineHeight: 1.1 }}>{ms.label}</Typography>
                      <Typography sx={{ fontSize: 11, fontWeight: 600 }} color="text.secondary">{l.currency}</Typography>
                    </Box>
                  </Stack>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography sx={{ fontFamily: MONO, fontSize: 16, fontWeight: 800, color: ms.fg }}>
                      {money(l.amount, l.currency)}
                    </Typography>
                    {l.currency !== "THB" && (
                      <Typography sx={{ fontSize: 11 }} color="text.secondary">= {money(l.amountTHB)}</Typography>
                    )}
                  </Box>
                </Box>

                {photos.length > 0 ? (
                  <Box sx={{ p: 1.5, display: "grid", gridTemplateColumns: photos.length > 1 ? "1fr 1fr" : "1fr", gap: 1 }}>
                    {photos.map((photo, pi) => (
                      <Box
                        key={photo.filename ?? pi}
                        onClick={() => onEnlarge && onEnlarge(photo.filename)}
                        sx={{ position: "relative", cursor: "zoom-in", "&:hover .slipHint": { opacity: 1 } }}
                      >
                        <AuthenticatedImage
                          src={getEvidenceUrl(photo.filename)}
                          alt={t("financial.slip")}
                          style={{ width: "100%", height: "auto", maxHeight: "40vh", objectFit: "contain", borderRadius: 8, border: "1px solid #eee", display: "block" }}
                        />
                        <Box
                          className="slipHint"
                          sx={{
                            position: "absolute", bottom: 14, right: 14,
                            display: "flex", alignItems: "center", gap: 0.5,
                            px: 1, py: 0.5, borderRadius: "8px",
                            background: "rgba(20,18,30,.72)", color: "#fff", fontSize: 11, fontWeight: 600,
                            opacity: 0.85, transition: "opacity .15s", pointerEvents: "none",
                          }}
                        >
                          <ZoomInRounded sx={{ fontSize: 15 }} />
                          {t("financial.enlarge")}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ p: 1.5 }}>
                    <Typography sx={{ fontSize: 11.5 }} color="text.secondary">{t("financial.noSlip")}</Typography>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </>
  );
}

// Full-screen slip viewer. Renders nothing visible until `filename` is set.
// `getEvidenceUrl` defaults to the manager-scoped /api/financial/evidence — pass
// `branchCashEvidenceUrl` (api/branchCash) for the station-scoped Branch Cash page.
export function SlipLightbox({ filename, onClose, t, getEvidenceUrl = evidenceUrl }) {
  return (
    <Dialog
      open={!!filename}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { bgcolor: "rgba(0,0,0,.92)", boxShadow: "none", m: { xs: 1, sm: 2 } } }}
    >
      <Box sx={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh", p: 2 }}>
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", top: 8, right: 8, color: "#fff", bgcolor: "rgba(255,255,255,.15)", "&:hover": { bgcolor: "rgba(255,255,255,.3)" } }}
        >
          <Close />
        </IconButton>
        {filename && (
          <AuthenticatedImage
            src={getEvidenceUrl(filename)}
            alt={t("financial.slip")}
            style={{ maxWidth: "100%", maxHeight: "85vh", objectFit: "contain" }}
          />
        )}
      </Box>
    </Dialog>
  );
}
